// lib/markdown.ts
//
// Render-time markdown preprocessor.
// - Encodes whitespace and HTML-unsafe chars in image/link URLs so marked
//   can tokenize URLs that contain literal spaces.
// - Promotes [text](image-url) to ![text](image-url) when the URL points
//   to an image file.
// - Skips fenced code blocks and inline code spans.
// - Fixes reference-style link definitions that contain spaces.
// - Escapes stray ~ outside of URLs to prevent accidental strikethrough.
// - Does NOT modify saved markdown; call this only at the marked.parse
//   boundary. (Turndown round-trips will eventually persist the encoded
//   form, which is canonical and equivalent.)

import { marked } from 'marked';

const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|svg|bmp|avif)$/i;

// marked가 생성하는 <img> 태그에 referrerpolicy 추가 — 핫링크 방지 우회
const renderer = new marked.Renderer();
const originalImage = renderer.image;
renderer.image = (href, title, text) => {
  const html = originalImage.call(renderer, href, title, text);
  return html.replace(/<img /, '<img referrerpolicy="no-referrer" ');
};
marked.use({ renderer });

const LINK_RE = /(!?)\[((?:[^\[\]\\]|\\.)*)\]\(/g;
const REF_DEF_RE = /^(\s*\[[^\]]+\]:\s*)(.+)$/gm;

function maskCode(md: string): { masked: string; stash: string[] } {
  const stash: string[] = [];
  const tok = (s: string) => {
    const id = stash.length;
    stash.push(s);
    return `CODE${id}`;
  };
  let masked = md.replace(
    /(^|\n)([ ]{0,3})(```+|~~~+)([^\n]*\n[\s\S]*?\n)\2\3(?=\n|$)/g,
    (m) => tok(m),
  );
  masked = masked.replace(
    /(`+)(?:[^`]|[^`][\s\S]*?[^`])\1(?!`)/g,
    (m) => tok(m),
  );
  return { masked, stash };
}

function restoreCode(s: string, stash: string[]): string {
  return s.replace(/CODE(\d+)/g, (_, i) => stash[Number(i)]);
}

function splitUrlAndTitle(inside: string): { url: string; title: string } {
  const m = inside.match(
    /^([\s\S]*?)\s+("(?:\\.|[^"\\])*"|'(?:\\.|[^'\\])*'|\((?:\\.|[^)\\])*\))\s*$/,
  );
  if (m) return { url: m[1].trim(), title: m[2] };
  return { url: inside.trim(), title: '' };
}

function unwrapAngle(url: string): string {
  if (url.length >= 2 && url.startsWith('<') && url.endsWith('>')) {
    return url.slice(1, -1);
  }
  return url;
}

function encodeUrlChars(url: string): string {
  // Encode whitespace/HTML-unsafe chars; leave % alone (no double-encoding).
  return url.replace(/[ \t<>"`]/g, (c) => {
    switch (c) {
      case ' ':
        return '%20';
      case '\t':
        return '%09';
      case '<':
        return '%3C';
      case '>':
        return '%3E';
      case '"':
        return '%22';
      case '`':
        return '%60';
      default:
        return c;
    }
  });
}

function pathLooksLikeImage(url: string): boolean {
  const noQuery = url.split(/[?#]/)[0];
  return IMAGE_EXT_RE.test(noQuery);
}

/** Find the matching `)` for a `(` that starts at `startIndex` inside `src`. */
function findMatchingParen(src: string, startIndex: number): number {
  let depth = 1;
  let i = startIndex;
  while (i < src.length && depth > 0) {
    if (src[i] === '(') depth++;
    else if (src[i] === ')') depth--;
    i++;
  }
  return depth === 0 ? i - 1 : -1;
}

export function prepareMarkdownForRender(markdown: string): string {
  if (!markdown) return markdown;

  const { masked, stash } = maskCode(markdown);

  // 1. Fix inline links/images by scanning for matching parens.
  let transformed = '';
  let lastIndex = 0;
  let m: RegExpExecArray | null;

  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(masked)) !== null) {
    const matchStart = m.index;
    const fullMatch = m[0];
    const bang = m[1];
    const alt = m[2];
    const parenOpenIndex = matchStart + fullMatch.length;

    const parenCloseIndex = findMatchingParen(masked, parenOpenIndex);
    if (parenCloseIndex === -1) continue; // unmatched parens — skip

    // Prevent matching inside an already-processed replacement.
    if (parenCloseIndex <= parenOpenIndex) continue;

    // Escaped bang: `\![..](..)` — leave alone.
    if (bang === '' && matchStart > 0 && masked[matchStart - 1] === '\\') {
      continue;
    }

    const inside = masked.slice(parenOpenIndex, parenCloseIndex);
    const { url: rawUrl, title } = splitUrlAndTitle(inside);
    const url = unwrapAngle(rawUrl);
    if (!url) continue;

    const encoded = encodeUrlChars(url);
    const isImageByExt = pathLooksLikeImage(url);
    const finalBang = bang === '!' ? '!' : isImageByExt ? '!' : '';
    const titlePart = title ? ` ${title}` : '';

    const replacement = `${finalBang}[${alt}](${encoded}${titlePart})`;

    transformed += masked.slice(lastIndex, matchStart) + replacement;
    lastIndex = parenCloseIndex + 1;

    // Advance regex past this match so we don't re-scan inside the replacement.
    LINK_RE.lastIndex = lastIndex;
  }
  transformed += masked.slice(lastIndex);

  // 2. Fix reference-style link definitions that contain spaces.
  transformed = transformed.replace(
    REF_DEF_RE,
    (full, prefix: string, rest: string) => {
      const { url: rawUrl, title } = splitUrlAndTitle(rest);
      const url = unwrapAngle(rawUrl);
      if (!url) return full;
      const encoded = encodeUrlChars(url);
      const titlePart = title ? ` ${title}` : '';
      return `${prefix}${encoded}${titlePart}`;
    },
  );

  // 3. Escape remaining ~ outside of code blocks and URLs.
  //    URLs already have ~ preserved (encodeUrlChars doesn't touch ~),
  //    and alt text was escaped above. A global replace is safe because
  //    \~ inside URLs is interpreted as literal ~ by marked.
  transformed = transformed.replace(/~/g, '\\~');

  return restoreCode(transformed, stash);
}
